<template>
  <div class="plugin-pricing-container">
    <!-- 顶部标题区域 -->
    <div class="pricing-header">
      <div class="header-content">
        <h1 class="pricing-title">{{ plugin.name }} - 定价方案</h1>
        <p class="pricing-subtitle">选择最适合您需求的方案，开启高效工作体验</p>
      </div>
    </div>

    <!-- 定价周期选择 -->
    <div class="pricing-content">
      <div class="period-selection">
        <div class="period-options">
          <div
              v-for="period in periodOptions"
              :key="period.days"
              class="period-option"
              :class="{
              active: selectedDays === period.days,
              'free-option': period.type === 'free',
              'popular-option': period.popular
            }"
              @click="onPeriodChange(period.days)"
          >
            <!-- 热门标签 -->
            <div class="period-badge" v-if="period.badge">
              {{ period.badge }}
            </div>

            <!-- 周期标题 -->
            <div class="period-header">
              <div class="period-desc">{{ period.desc }}</div>
              <div class="period-duration">{{ period.duration }}</div>
            </div>

            <!-- 价格区域 -->
            <div class="price-section" v-if="period.type === 'paid'">
              <div class="original-price" v-if="period.originalPrice > period.currentPrice">
                ¥{{ period.originalPrice.toFixed(2) }}
              </div>
              <div class="current-price">
                ¥{{ period.currentPrice.toFixed(2) }}
              </div>
              <div class="savings">
                {{ period.savings }}
              </div>
            </div>

            <div class="price-section" v-else>
              <div class="free-price">免费体验</div>
              <div class="savings">{{ period.savings }}</div>
            </div>

            <!-- 功能特性 -->
            <ul class="features-list">
              <li v-for="(feature, index) in period.features" :key="index">
                <el-icon><Check /></el-icon>
                {{ feature }}
              </li>
            </ul>

            <!-- 操作按钮 -->
            <div class="action-button">
              <span v-if="period.type === 'free'">立即下载体验</span>
              <span v-else>立即购买</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 登录模态框 -->
    <LoginModal
        v-model="showLoginModal"
        :payment-info="currentPaymentInfo"
        @login-success="handleLoginSuccess"
        @switch-to-register="switchToRegister"
    />

    <!-- 注册模态框 -->
    <RegisterModal
        v-model="showRegisterModal"
        :payment-info="currentPaymentInfo"
        @register-success="handleRegisterSuccess"
        @switch-to-login="switchToLogin"
    />

    <!-- 支付弹窗 -->
    <el-dialog
        v-model="showPayDialog"
        :title="getSelectedPeriod().desc"
        width="500px"
        :before-close="handleClose"
        :close-on-click-modal="false"
        :close-on-press-escape="false"
    >
      <div class="pay-dialog-content">
        <div v-if="selectedDays === 0" class="free-trial-content">
          <el-icon color="#52c41a" :size="60"><Check /></el-icon>
          <h3>已选择免费体验方案</h3>
          <p>点击确认后开始下载插件，享受14天完整功能体验</p>
        </div>

        <div v-else class="payment-content">
          <div class="selected-plan-info">
            <h4>{{ getSelectedPeriod().desc }}</h4>
            <p class="total-amount">总计：¥{{ calculatePrice(selectedDays) }}</p>
          </div>

          <div class="payment-methods">
            <div class="payment-title">选择支付方式</div>
            <div class="payment-options">
              <div class="payment-option wechat-payment active">
                <svg t="1761617723278" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4874" width="24" height="24">
                  <path d="M1024 619.52c0-143.36-138.24-256-307.2-256s-307.2 112.64-307.2 256 138.24 256 307.2 256c30.72 0 61.44-5.12 92.16-10.24l97.28 51.2-25.6-76.8c87.04-51.2 143.36-128 143.36-220.16z m-414.72-40.96c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z m209.92 0c-30.72 0-51.2-20.48-51.2-51.2s20.48-51.2 51.2-51.2 51.2 20.48 51.2 51.2c0 25.6-25.6 51.2-51.2 51.2z" fill="#4CBF00" p-id="4875"></path>
                  <path d="M358.4 609.28c0-158.72 153.6-286.72 348.16-286.72h15.36c-40.96-133.12-179.2-235.52-353.28-235.52-204.8 0-368.64 138.24-368.64 307.2 0 107.52 66.56 204.8 168.96 256l-30.72 92.16L256 686.08c35.84 10.24 71.68 15.36 112.64 15.36h10.24c-15.36-30.72-20.48-61.44-20.48-92.16z m138.24-414.72c35.84 0 66.56 30.72 66.56 66.56s-30.72 66.56-66.56 66.56C460.8 322.56 430.08 291.84 430.08 256S460.8 194.56 496.64 194.56zM245.76 322.56c-35.84 0-61.44-30.72-61.44-66.56s30.72-66.56 66.56-66.56 61.44 30.72 61.44 66.56-30.72 66.56-66.56 66.56z" fill="#4CBF00" p-id="4876"></path>
                </svg>
                <span>微信支付</span>
              </div>
            </div>
          </div>

          <!-- 二维码支付区域 -->
          <div class="qrcode-section" v-if="payQrCode">
            <p>请使用微信扫描下方二维码完成支付</p>
            <div class="qrcode-container">
              <img :src="payQrCode" alt="微信支付二维码" class="qrcode-image" />
              <div v-if="paymentStatus === 'success'" class="success-overlay">
                <el-icon color="#52c41a" :size="80"><Check /></el-icon>
                <p>支付成功</p>
              </div>
            </div>
            <div class="polling-hint" v-if="paymentStatus === 'pending'">
              <el-icon><Loading /></el-icon>
              <span>等待支付中... 支付成功后自动跳转</span>
            </div>
          </div>

          <!-- 加载状态 -->
          <div class="loading-qrcode" v-if="!payQrCode && paymentStatus === 'pending'">
            <el-icon><Loading /></el-icon>
            <span>正在生成支付二维码...</span>
          </div>
        </div>
      </div>

      <template #footer>
        <span class="dialog-footer">
<!--          <el-button @click="handleCloseOrder(payOrderInfo.outTradeNo)"-->
<!--                     v-if="selectedDays > 0 && payOrderInfo.outTradeNo && paymentStatus !== 'success'">-->
<!--            取消订单-->
<!--          </el-button>-->
          <el-button type="primary"
                     @click="confirmPayment"
                     :disabled="selectedDays > 0 && paymentStatus !== 'success'">
            {{ selectedDays === 0 ? '立即下载体验' : (paymentStatus === 'success' ? '完成支付并下载' : '等待支付') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, markRaw, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import LoginModal from '@/components/LoginModal.vue'
import RegisterModal from '@/components/RegisterModal.vue'
import BindPhoneDialog from '@/components/BindPhoneDialog.vue'
import QRCode from 'qrcode'
import request from '@/utils/request'
import { ElMessage } from 'element-plus'
import {
  Check, ChatDotRound, Loading
} from '@element-plus/icons-vue'


// 获取路由参数中的插件ID
const route = useRoute()
const router = useRouter()
const pluginId = ref(parseInt(route.query.id))

// 支付相关数据
const showPayDialog = ref(false)
const selectedDays = ref(null)   // 改为 null，不默认选中任何周期
const payQrCode = ref('')
const paymentStatus = ref('pending')
const pollInterval = ref(null)
const currentOrderNo = ref('')   // 当前订单号

// 注册登录相关状态
const showLoginModal = ref(false)
const showRegisterModal = ref(false)
const currentPaymentInfo = ref({})

// 添加滚动锁定状态
const isScrollLocked = ref(false)

// 插件数据（与PluginDetails.vue中相同的结构）
const allPlugins = ref([
  {
    id: 1978297871453697,
    name: '易比图--像素级图片比对，可调参数高亮差异点',
    description: '让差异无处遁形，一眼发现不同！',
    price: 9.9,
    sales: 6888,
    rating: 4.8,
    image: require('@/assets/yibi.gif'),
    type: '校对工具',
    software: 'Adobe Illustrator',
    author: '智能图像实验室',
    authorAvatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '热门',
    tagColor: '#ff4d4f',
    media: require('@/assets/yibi.gif'),
  },
  {
    id: 1,
    name: '企业级二维码生成器--智能编码与品牌管理',
    description: '支持多种二维码标准与定制化生成，满足企业多样化编码需求',
    price: 29,
    sales: 1284,
    rating: 4.7,
    image: require('@/assets/code.gif'),
    type: '编码工具',
    software: 'Adobe Illustrator',
    author: '编码技术实验室',
    authorAvatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '热门',
    tagColor: '#ff4d4f',
    media: require('@/assets/code.gif'),

  },
  {
    id: 2,
    name: '智能刀模生成插件--让包装设计效率飞升',
    description: '输入尺寸自动生成精准刀模线，支持复杂盒型设计',
    price: 49,
    sales: 985,
    rating: 4.8,
    image: require('@/assets/mo.gif'),
    type: '设计工具',
    software: 'Adobe Illustrator',
    author: '包装专家团队',
    authorAvatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '新品',
    tagColor: '#52c41a',
    media: require('@/assets/mo.gif'),

  },
  {
    id: 3,
    name: '智能能效标签管理插件--数据零差错，合规全自动',
    description: '自动获取数据并填充，支持20+国际标准模板',
    price: 39,
    sales: 756,
    rating: 4.6,
    image: require('@/assets/neng.gif'),
    type: '数据工具',
    software: 'Adobe InDesign, Microsoft Excel',
    author: '数据科技公司',
    authorAvatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '必备',
    tagColor: '#1890ff',
    media: require('@/assets/neng.gif'),

  },
  {
    id: 4,
    name: '美国版能效标签三角箭头智能定位插件',
    description: '自动计算三角箭头在能效标尺的准确位置',
    price: 59,
    sales: 632,
    rating: 4.5,
    image: require('@/assets/huang.gif'),
    type: '专业工具',
    software: 'Adobe Illustrator, Adobe Photoshop',
    author: '能效标准实验室',
    authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80',
    tag: '专业',
    tagColor: '#722ed1',
    media: require('@/assets/huang.gif'),
  }
])

// 根据ID获取当前插件
const plugin = computed(() => {
  return allPlugins.value.find(p => p.id === pluginId.value) || allPlugins.value[0]
})

// 周期选项
const periodOptions = computed(() => {
  const price = plugin.value?.price || 0
  return [
    {
      days: 0,
      desc: '免费体验',
      type: 'free',
      duration: '14天',
      originalPrice: 0,
      currentPrice: 0,
      savings: '零成本试用',
      features: [
        '完整功能体验',
        '无使用限制',
        '一键下载安装',
        '适合初次体验用户'
      ],
      badge: '推荐',
      popular: true
    },
    {
      days: 30,
      desc: '月卡套餐',
      type: 'paid',
      duration: '30天',
      originalPrice: 15,
      currentPrice: 15,
      savings: '灵活度高',
      features: [
        '适合短期项目',
        '按需灵活购买',
        '性价比适中',
        '随时可升级'
      ],
      badge: '灵活',
      popular: false
    },
    {
      days: 90,
      desc: '季卡套餐',
      type: 'paid',
      duration: '90天',
      originalPrice: 36,
      currentPrice: 36, // 9折
      savings: '稳定省心',
      features: [
        '性价比最高',
        '长期项目首选',
        '节省10%费用',
        '稳定使用体验'
      ],
      badge: '超值',
      popular: true
    },
    {
      days: 365,
      desc: '年卡套餐',
      type: 'paid',
      duration: '365天',
      originalPrice: 120,
      currentPrice: 120, // 75折
      savings: '尊享优惠',
      features: [
        '最大优惠力度',
        '企业用户首选',
        '节省20%费用',
        '全年免费更新',
        '优先技术支持'
      ],
      badge: '尊享',
      popular: false
    }
  ]
})

// 存储订单信息
const payOrderInfo = ref({
  outTradeNo: '',
  productName: '',
  amount: 0,
  days: 0,
  timeExpire: ''
})

// 计算价格函数
const calculatePrice = (days) => {
  const period = periodOptions.value.find(p => p.days === days)
  if (period) {
    return period.currentPrice.toFixed(2)
  }
}

// 获取选中的周期信息
const getSelectedPeriod = () => {
  return periodOptions.value.find(period => period.days === selectedDays.value) || periodOptions.value[0]
}

// 周期选择事件
const onPeriodChange = async (days) => {
  selectedDays.value = days

  // 查找选中的周期选项
  const selectedPeriod = periodOptions.value.find(period => period.days === days)

  if (selectedPeriod.type === 'free') {
    // 免费体验：直接触发下载，不需要登录
    await handleFreeTrial()
  } else {
    // 付费周期：检查登录状态
    const isLoggedIn = checkUserLogin()

    if (!isLoggedIn) {
      // 未登录：保存支付信息并显示登录弹窗
      currentPaymentInfo.value = {
        days: days,
        period: selectedPeriod,
        plugin: plugin.value
      }
      showLoginModal.value = true
    } else {
      // 已登录：显示支付弹窗
      showPayDialog.value = true
      await handlePaidPeriod(days)
    }
  }

}

// 处理免费体验
const handleFreeTrial = async () => {
  try {
    ElMessage.success('开始免费体验，正在下载插件...')

    // 记录下载信息
    await recordDownload(plugin.value.id);

    // 触发下载
    window.location.href = '/api/downPlugin/download-exe'
  } catch (error) {
    console.error('免费体验选择失败:', error)
    ElMessage.error('下载失败，请重试')
  }
}

// 处理付费周期
const handlePaidPeriod = async (days) => {
  // 如果存在未支付的旧订单，先关闭它
  if (currentOrderNo.value && paymentStatus.value === 'pending') {
    await handleCloseOrder(currentOrderNo.value)
  }

  // 清除当前支付状态和二维码
  payQrCode.value = ''
  paymentStatus.value = 'pending'
  currentOrderNo.value = ''

  // 重新生成支付二维码
  await handlePurchase(days)
}

// 生成支付二维码
const handlePurchase = async (days) => {
  selectedDays.value = days
  paymentStatus.value = 'pending'
  payQrCode.value = ''

  try {
    const response = await request.post('/pay/createQrCode', {
      productId: plugin.value.id,
      productName: plugin.value.name,
      days: days,
      amount: calculatePrice(days),
      description: `购买${plugin.value.name} ${days}天使用权`,
      paymentType: 'wechat_native'
    })

    if (response.data.success) {
      // 生成二维码图片
      const qrCodeDataURL = await QRCode.toDataURL(response.data.qrCodeUrl, {
        width: 200,
        margin: 1
      })
      payQrCode.value = qrCodeDataURL

      // 保存当前订单号
      currentOrderNo.value = response.data.outTradeNo

      // 启动轮询
      startPolling(response.data.outTradeNo)

      // 保存订单信息
      payOrderInfo.value = {
        outTradeNo: response.data.outTradeNo,
        productName: plugin.value.name,
        amount: calculatePrice(days),
        days: days,
        timeExpire: response.data.timeExpire || ''
      }

      console.log('支付创建成功：', response.data)
    } else {
      // ElMessage.error(response.data.message || '支付请求失败')
    }
  } catch (error) {
    console.error('支付请求失败:', error)
    // ElMessage.error('支付请求失败，请重试')
  }
}

// 轮询支付状态
const startPolling = (outTradeNo) => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value)
  }

  pollInterval.value = setInterval(async () => {
    try {
      const response = await request.get(`/pay/pollStatus/${outTradeNo}`)

      if (response.data.success && response.data.paid) {
        // 支付成功
        paymentStatus.value = 'success'
        clearInterval(pollInterval.value)
        ElMessage.success('支付成功！')
        // 支付成功后清除当前订单号
        currentOrderNo.value = ''
      }
    } catch (error) {
      console.error('轮询支付状态失败:', error)
    }
  }, 3000)
}

// 取消订单
const handleCloseOrder = async (orderNo = null) => {
  const outTradeNo = orderNo || payOrderInfo.value.outTradeNo
  if (!outTradeNo) {
    ElMessage.warning('没有可关闭的订单')
    return
  }

  try {
    const response = await request.post(`/pay/closeOrder/${outTradeNo}`)
    if (response.data.success) {
      ElMessage.success('订单已成功关闭')

      // 如果关闭的是当前订单，清除相关信息
      if (outTradeNo === currentOrderNo.value) {
        payOrderInfo.value = {
          outTradeNo: '',
          productName: '',
          amount: 0,
          days: 0,
          timeExpire: ''
        }
        payQrCode.value = ''
        paymentStatus.value = 'pending'
        currentOrderNo.value = ''
      }
    } else {
      ElMessage.error('关闭订单失败: ' + response.data.message)
    }
  } catch (error) {
    console.error('关闭订单失败:', error)
    ElMessage.error('关闭订单失败，请重试')
  }
}

// 关闭弹窗
const handleClose = (done) => {
  showPayDialog.value = false
  payQrCode.value = ''
  paymentStatus.value = 'pending'

  // 关闭弹窗时也关闭未支付的订单
  if (currentOrderNo.value && paymentStatus.value === 'pending') {
    handleCloseOrder(currentOrderNo.value)
  }

  if (pollInterval.value) {
    clearInterval(pollInterval.value)
    pollInterval.value = null
  }

  // 关闭弹窗后重置选中状态
  selectedDays.value = null

  done()
}

// 完成支付
const confirmPayment = () => {
  if (selectedDays.value === 0) {
    // 免费周期：触发下载
    recordDownload(plugin.value.id).then(() => {
      window.location.href = '/api/downPlugin/download-exe'
    });
    ElMessage.success('开始免费体验，正在下载插件...')
    showPayDialog.value = false
  } else if (selectedDays.value > 0) {
    // 付费周期：支付成功后的操作
    recordDownload(plugin.value.id).then(() => {
      window.location.href = '/api/downPlugin/download-exe'
    });
    ElMessage.success('支付成功！即将开始下载')
    showPayDialog.value = false
  }
}

// 下载记录
const recordDownload = async (productId) => {
  console.log(productId)
  try {
    await request.post(`/plugin/record/download?productId=${productId}`);
    console.log('下载记录保存成功');
  } catch (error) {
    console.error('保存下载记录失败:', error);
    // 这里不提示用户，避免影响下载体验
  }
};

// 检查用户登录状态
const checkUserLogin = () => {
  const token = localStorage.getItem('auth_token')
  return !!token
}

// 优化的滚动锁定函数
const lockScroll = () => {
  if (isScrollLocked.value) return

  const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
  document.body.style.overflow = 'hidden'
  document.body.style.paddingRight = `${scrollBarWidth}px`
  isScrollLocked.value = true
}

const unlockScroll = () => {
  if (!isScrollLocked.value) return

  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
  isScrollLocked.value = false
}

// 优化弹窗切换函数，确保无间隙切换
const switchToRegister = () => {
  // 先锁定滚动，再切换弹窗
  lockScroll()
  showLoginModal.value = false
  // 使用 nextTick 确保在下一个 DOM 更新周期打开注册弹窗
  nextTick(() => {
    showRegisterModal.value = true
  })
}

const switchToLogin = () => {
  // 先锁定滚动，再切换弹窗
  lockScroll()
  showRegisterModal.value = false
  nextTick(() => {
    showLoginModal.value = true
  })
}

// 弹窗状态监听器
watch([showLoginModal, showRegisterModal], ([loginVisible, registerVisible]) => {
  if (loginVisible || registerVisible) {
    lockScroll()
  } else {
    // 使用 setTimeout 确保所有弹窗都完全关闭后再解锁滚动
    setTimeout(() => {
      if (!showLoginModal.value && !showRegisterModal.value) {
        unlockScroll()
      }
    }, 50)
  }
})

// 弹窗关闭处理函数 - 登录成功处理
const handleLoginSuccess = async (userInfo) => {
  const { paymentInfo } = userInfo

  if (paymentInfo) {
    currentPaymentInfo.value = paymentInfo
    selectedDays.value = paymentInfo.days

    // 添加短暂延迟确保用户信息完全加载
    await new Promise(resolve => setTimeout(resolve, 300));

    showPayDialog.value = true
    handlePaidPeriod(paymentInfo.days)
  }

  showLoginModal.value = false
}
// 弹窗关闭处理函数 - 注册成功处理
const handleRegisterSuccess = async (userInfo) => {
  const { paymentInfo } = userInfo

  if (paymentInfo) {
    currentPaymentInfo.value = paymentInfo
    selectedDays.value = paymentInfo.days

    // 添加短暂延迟确保用户信息完全加载
    await new Promise(resolve => setTimeout(resolve, 300));

    showPayDialog.value = true
    handlePaidPeriod(paymentInfo.days)
  }

  showRegisterModal.value = false
}

// 在组件卸载时清除轮询
onBeforeUnmount(() => {
  unlockScroll()

  if (pollInterval.value) {
    clearInterval(pollInterval.value)
  }
})
</script>

<style scoped>
/* 防止弹窗打开时背景滚动 */
.plugin-pricing-container {
  position: relative;
}

/* 当有弹窗打开时，锁定页面滚动 */
body.dialog-open {
  overflow: hidden !important;
}

/* 弹窗遮罩层虚化效果 */
:deep(.el-overlay) {
  backdrop-filter: blur(4px);
}

.plugin-pricing-container {
  min-height: 100vh;
  background: linear-gradient(135deg,
  rgba(54, 81, 191, 0.08) 0%,
  rgba(226, 103, 216, 0.06) 30%,
  rgba(235, 110, 13, 0.05) 70%,
  rgba(54, 81, 191, 0.04) 100%);
  padding: 0 20px;
}

/* 顶部标题样式 */
.pricing-header {
  text-align: center;
  padding: 60px 0 40px;
}

.pricing-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg,
  #0078d7 0%,
  #6a5af9 25%,
  #d16ba5 50%,
  #ff8c00 75%,
  #ff6b00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.pricing-subtitle {
  font-size: 1.2rem;
  color: #64748b;
}

/* 定价内容样式 */
.pricing-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0 60px;
}

.period-selection {
  margin: 40px 0;
}

.period-options {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 20px;
}

/* 周期选项样式 */
.period-option {
  position: relative;
  padding: 30px 24px 30px; /* 增加底部内边距，为按钮留出空间 */
  border: 2px solid #e4e7ed;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  text-align: center;
  display: flex;
  flex-direction: column;
  height: 100%; /* 确保高度一致 */
  min-height: 400px; /* 设置最小高度 */
}

.period-option:hover {
  border-color: #409eff;
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.period-option.active {
  border-color: #409eff;
  background: linear-gradient(135deg, #ecf5ff, #f0f9ff);
}

.period-option.popular-option {
  border-color: #ff6b6b;
}

.period-badge {
  position: absolute;
  top: -10px;
  right: 20px;
  background: linear-gradient(45deg, #ff6b6b, #ff8e6b);
  color: white;
  padding: 6px 16px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: bold;
}

.period-header {
  text-align: center;
  margin-bottom: 20px;
}

.period-desc {
  font-size: 20px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 6px;
}

.period-duration {
  font-size: 14px;
  color: #909399;
}

.price-section {
  text-align: center;
  margin-bottom: 20px;
}

.original-price {
  font-size: 14px;
  color: #909399;
  text-decoration: line-through;
  margin-bottom: 4px;
}

.current-price {
  font-size: 28px;
  font-weight: bold;
  color: #f56c6c;
  margin-bottom: 6px;
}

.free-price {
  font-size: 28px;
  font-weight: bold;
  color: #52c41a;
  margin-bottom: 6px;
}

.savings {
  font-size: 12px;
  color: #52c41a;
  background: #f6ffed;
  padding: 4px 12px;
  border-radius: 12px;
  display: inline-block;
}

.features-list {
  list-style: none;
  padding: 0;
  margin: 0 0 auto 0; /* 使用 auto 让特性列表占据剩余空间 */
  text-align: left;
  flex-grow: 1; /* 让特性列表可以扩展 */
}

.features-list li {
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: #606266;
}

.features-list .el-icon {
  color: #52c41a;
  margin-right: 10px;
  font-size: 16px;
}

.action-button {
  position: absolute; /* 改为绝对定位 */
  bottom: 20px; /* 距离底部20px */
  left: 20px;
  right: 20px;
  text-align: center;
  padding: 12px 0;
  background: #409eff;
  color: white;
  border-radius: 8px;
  font-weight: 500;
  font-size: 16px;
  transition: all 0.3s;
  margin-top: auto; /* 确保按钮在底部 */
}

.period-option.active .action-button {
  background: #ff6b6b;
}

/* 免费选项特殊样式 */
.free-option {
  border-color: #52c41a;
}

.free-option.active {
  border-color: #52c41a;
  background: linear-gradient(135deg, #f6ffed, #f0fff3);
}

.free-option .action-button {
  background: #52c41a;
}

.free-option.active .action-button {
  background: #52c41a;
}

/* 支付弹窗样式 */
.pay-dialog-content {
  text-align: center;
}

.free-trial-content {
  padding: 20px;
}

.free-trial-content h3 {
  margin: 16px 0 8px;
  color: #303133;
}

.free-trial-content p {
  color: #606266;
  line-height: 1.6;
}

.payment-content {
  padding: 10px 0;
}

.selected-plan-info {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.selected-plan-info h4 {
  margin: 0 0 8px 0;
  color: #303133;
}

.total-amount {
  font-size: 20px;
  font-weight: bold;
  color: #f56c6c;
  margin: 0;
}

.payment-methods {
  margin: 20px 0;
}

.payment-title {
  text-align: left;
  margin-bottom: 12px;
  font-weight: bold;
  color: #303133;
}

.payment-options {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.payment-option {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.payment-option.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.payment-option .icon {
  margin-right: 8px;
  font-size: 20px;
}

.wechat-payment .icon {
  color: #07c160;
}

.qrcode-section {
  margin: 20px 0;
}

.qrcode-container {
  position: relative;
  display: inline-block;
  margin: 10px 0;
}

.qrcode-image {
  width: 200px;
  height: 200px;
  border: 1px solid #eee;
}

.success-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
}

.success-overlay p {
  margin-top: 10px;
  font-weight: bold;
  color: #52c41a;
}

.polling-hint {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.polling-hint .el-icon {
  margin-right: 5px;
  animation: rotating 2s linear infinite;
}

.loading-qrcode {
  margin: 40px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #666;
}

.loading-qrcode .el-icon {
  font-size: 40px;
  color: #409eff;
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .period-options {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .pricing-title {
    font-size: 2rem;
  }

  .period-option {
    padding: 20px 16px;
  }
}

@media (max-width: 480px) {
  .plugin-pricing-container {
    padding: 0 16px;
  }

  .pricing-title {
    font-size: 1.8rem;
  }

  .pricing-subtitle {
    font-size: 1rem;
  }
}
</style>